import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader, RefreshCw } from 'lucide-react';
import { getStatusColor, getStatusLabel, type ApiHealthResult } from '@/lib/api-health';

interface ApiConnectionStatusProps {
  apiName: string;
  checkFunction: () => Promise<ApiHealthResult>;
  autoCheck?: boolean;
  className?: string;
}

export function ApiConnectionStatus({ 
  apiName, 
  checkFunction, 
  autoCheck = false,
  className = ''
}: ApiConnectionStatusProps) {
  const [result, setResult] = useState<ApiHealthResult | null>(null);
  const [testing, setTesting] = useState(false);

  React.useEffect(() => {
    if (autoCheck) {
      testConnection();
    }
  }, [autoCheck]);

  const testConnection = async () => {
    setTesting(true);
    try {
      const healthResult = await checkFunction();
      setResult(healthResult);
    } catch (error) {
      setResult({
        status: 'error',
        message: 'Connection test failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = () => {
    if (testing) {
      return <Loader className="w-4 h-4 animate-spin text-yellow-600" />;
    }
    
    if (!result) {
      return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }

    switch (result.status) {
      case 'connected':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'configured':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'unconfigured':
        return <XCircle className="w-4 h-4 text-gray-400" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBgColor = () => {
    if (!result) return 'bg-gray-50 border-gray-200';
    
    switch (result.status) {
      case 'connected':
        return 'bg-green-50 border-green-200';
      case 'configured':
        return 'bg-blue-50 border-blue-200';
      case 'unconfigured':
        return 'bg-gray-50 border-gray-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusTextColor = () => {
    if (!result) return 'text-gray-600';
    
    switch (result.status) {
      case 'connected':
        return 'text-green-700';
      case 'configured':
        return 'text-blue-700';
      case 'unconfigured':
        return 'text-gray-600';
      case 'error':
        return 'text-red-700';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${getStatusBgColor()} ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="font-semibold text-gray-900">{apiName}</span>
        </div>
        <button
          onClick={testConnection}
          disabled={testing}
          className="p-1 hover:bg-white/50 rounded transition-colors disabled:opacity-50"
          title="Test connection"
        >
          <RefreshCw className={`w-4 h-4 text-gray-600 ${testing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {result && (
        <div className="space-y-2">
          <div className={`text-sm font-medium ${getStatusTextColor()}`}>
            {getStatusLabel(result.status)}
          </div>
          <div className="text-sm text-gray-700">
            {result.message}
          </div>
          
          {result.error && (
            <div className="text-xs text-red-600 font-mono bg-red-100/50 p-2 rounded">
              {result.error}
            </div>
          )}
          
          {result.lastChecked && (
            <div className="text-xs text-gray-500">
              Last checked: {result.lastChecked.toLocaleTimeString()}
            </div>
          )}
          
          {result.details && Object.keys(result.details).length > 0 && (
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer hover:text-gray-900">Details</summary>
              <pre className="mt-2 p-2 bg-white/50 rounded overflow-x-auto">
                {JSON.stringify(result.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
      
      {!result && !testing && (
        <div className="text-sm text-gray-600">
          Click the refresh button to test connection
        </div>
      )}
    </div>
  );
}

// Compact version for inline display
interface ApiStatusBadgeProps {
  apiName: string;
  checkFunction: () => Promise<ApiHealthResult>;
  showLabel?: boolean;
}

export function ApiStatusBadge({ apiName, checkFunction, showLabel = true }: ApiStatusBadgeProps) {
  const [result, setResult] = useState<ApiHealthResult | null>(null);
  const [testing, setTesting] = useState(false);

  React.useEffect(() => {
    testConnection();
  }, []);

  const testConnection = async () => {
    setTesting(true);
    try {
      const healthResult = await checkFunction();
      setResult(healthResult);
    } catch (error) {
      setResult({
        status: 'error',
        message: 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  if (testing) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-50 border border-yellow-200 rounded-full text-sm">
        <Loader className="w-3 h-3 animate-spin text-yellow-600" />
        {showLabel && <span className="text-yellow-700">Testing...</span>}
      </div>
    );
  }

  if (!result) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-sm">
        <AlertCircle className="w-3 h-3 text-gray-400" />
        {showLabel && <span className="text-gray-600">Unknown</span>}
      </div>
    );
  }

  const getStatusConfig = () => {
    switch (result.status) {
      case 'connected':
        return {
          icon: <CheckCircle className="w-3 h-3 text-green-600" />,
          label: 'Connected',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-700'
        };
      case 'configured':
        return {
          icon: <CheckCircle className="w-3 h-3 text-blue-600" />,
          label: 'Configured',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-700'
        };
      case 'unconfigured':
        return {
          icon: <XCircle className="w-3 h-3 text-gray-400" />,
          label: 'Not Configured',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-600'
        };
      case 'error':
        return {
          icon: <XCircle className="w-3 h-3 text-red-600" />,
          label: 'Error',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-700'
        };
      default:
        return {
          icon: <AlertCircle className="w-3 h-3 text-gray-400" />,
          label: 'Unknown',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-600'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div 
      className={`inline-flex items-center gap-2 px-3 py-1 ${config.bgColor} border ${config.borderColor} rounded-full text-sm cursor-help`}
      title={result.message}
    >
      {config.icon}
      {showLabel && <span className={config.textColor}>{config.label}</span>}
    </div>
  );
}
